import React, {useEffect, useMemo, useState} from "react";
import {Button, Modal, Spinner} from "react-bootstrap";
import AppComponentCardHeader from "../../common/AppComponentCardHeader";
import LoadingDataDiv from "../../common/LoadingDataDiv";
import useCrud from "../../../../hooks/useCrud";
import useAxiosPrivate from "../../../../hooks/useAxiosPrivate";
import {ToastContainer} from "react-toastify";
import {toastError, toastSuccess} from "../../../../helpers/Toast";
import "../../../../css/SettingsForms.css";

const Settings = () => {
  const dataUrl = "/settings";
  const {updateItem, getItems} = useCrud(dataUrl);
  const axiosPrivate = useAxiosPrivate();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [itemsList, setItems] = useState([]);
  const [currentItem, setCurrentItem] = useState({});
  const [backupItem, setBackupItem] = useState({});
  const [invalidFields, setInvalidFields] = useState(new Set());
  const [folderBrowserOpen, setFolderBrowserOpen] = useState(false);
  const [folderBrowserLoading, setFolderBrowserLoading] = useState(false);
  const [folderBrowser, setFolderBrowser] = useState({currentPath: null, parentPath: null, directories: [], roots: true});
  const [weatherDirections, setWeatherDirections] = useState({latitude: "N", longitude: "E"});

  const itemDictionary = {
    infractionWeightMinimumValue: "Minimalna waga przewinień pracowników",
    infractionWeightMaximumValue: "Maksymalna waga przewinień pracowników",
    commendationWeightMinimumValue: "Minimalna waga pochwał pracowników",
    commendationWeightMaximumValue: "Maksymalna waga pochwał pracowników",
    skillWeightMinimumValue: "Minimalna waga umiejętności pracowników",
    skillWeightMaximumValue: "Maksymalna waga umiejętności pracowników",
    skillEvaluationMinimumValue: "Minimalna ocena umiejętności pracowników",
    skillEvaluationMaximumValue: "Maksymalna ocena umiejętności pracowników",
    trainingEvaluationMinimumValue: "Minimalna ocena ze szkolenia pracowników",
    trainingEvaluationMaximumValue: "Maksymalna ocena ze szkolenia pracowników",
    sameManagerInManyDepartments: "Ta sama osoba jako manager w wielu departamentach",
    employeeDocumentsRootPath: "Katalog dokumentów pracowników",
    positionEvaluationDefaultValue: "Domyślna ocena umiejętności na stanowisku",
    positionEvaluationMinimumValue: "Minimalna ocena umiejętności na stanowisku",
    positionEvaluationMaximumValue: "Maksymalna ocena umiejętności na stanowisku",
    employeeEvaluationSoftSkillsWeight: "Waga umiejętności miękkich w ocenie ogólnej",
    employeeEvaluationPositionsWeight: "Waga stanowisk w ocenie ogólnej",
    employeeEvaluationTrainingsWeight: "Waga szkoleń w ocenie ogólnej",
    employeeEvaluationTrainingTestsWeight: "Waga testów szkoleniowych w ocenie ogólnej",
    employeeEvaluationReviewsWeight: "Waga ocen rozwojowych i rozmów oceniających w ocenie ogólnej",
    employeeEvaluationEventsWeight: "Waga pochwał i przewinień w ocenie ogólnej",
    contractExpirationWarningDays: "Przypomnienie o wygasającej umowie (dni)",
    scheduleEmploymentMinimumDailyRestHours: "Minimalny odpoczynek dobowy dla umowy o pracę (h)",
    scheduleEmploymentMinimumWeeklyRestHours: "Minimalny odpoczynek tygodniowy dla umowy o pracę (h)",
    scheduleDefaultRequiredDaysOffPerWeek: "Globalna liczba dni wolnych w tygodniu",
    scheduleDefaultMaxConsecutiveWorkDays: "Globalna maksymalna liczba kolejnych dni pracy",
    scheduleConsecutiveDaysWarningThreshold: "Globalny próg ostrzeżenia o kolejnych dniach pracy",
    scheduleWeeklyHoursWarningPercent: "Próg ostrzeżenia o limicie godzin tygodniowych (%)",
    scheduleTrainingTestMonthlyFailureLimit: "Miesięczny limit niezaliczonych testów szkoleniowych",
    workTimeUnexcusedAbsenceAlertThreshold: "Alert po liczbie nieusprawiedliwionych nieobecności",
    rcpLateToleranceMinutes: "Tolerancja spóźnienia RCP (min)",
    rcpEarlyLeaveToleranceMinutes: "Tolerancja wcześniejszego wyjścia RCP (min)",
    rcpMissingStartAlertMinutes: "Alarm braku rozpoczęcia pracy RCP po (min)",
    rcpMaximumBreakMinutes: "Maksymalny łączny czas przerw w sesji RCP (min)",
    scheduleDayOffMinimumAdvanceDays: "Globalne minimalne wyprzedzenie prośby o wolne (dni)",
    scheduleVacationMinimumAdvanceDays: "Globalne minimalne wyprzedzenie urlopu (dni)",
    gangExportCharset: "Wapro Gang — kodowanie",
    gangExportDelimiter: "Wapro Gang — separator",
    gangExportIncludeHeader: "Wapro Gang — wiersz nagłówka",
    gangExportDateFormat: "Wapro Gang — format daty",
    gangExportTimeFormat: "Wapro Gang — format czasu",
    gangExportColumns: "Wapro Gang — mapowanie kolumn",
    scheduleRuleOverrideRequireReason: "Wymagaj uzasadnienia odstępstwa od reguł grafiku",
    weatherForecastEnabled: "Automatyczne pobieranie prognozy pogody",
    weatherLatitude: "Szerokość geograficzna (°)",
    weatherLongitude: "Długość geograficzna (°)",
    weatherTimezone: "Strefa czasowa",
    weatherProviderBaseUrl: "Adres dostawcy prognozy pogody",
    certificationsExpirationCron: "Harmonogram sprawdzania ważności certyfikatów"
  };

  const sections = [
    {title: "Ustawienia dla nagan", fields: ["infractionWeightMinimumValue", "infractionWeightMaximumValue"]},
    {title: "Ustawienia dla pochwał", fields: ["commendationWeightMinimumValue", "commendationWeightMaximumValue"]},
    {title: "Ustawienia dla umiejętności", fields: ["skillWeightMinimumValue", "skillWeightMaximumValue", "skillEvaluationMinimumValue", "skillEvaluationMaximumValue"]},
    {title: "Ustawienia dla szkoleń", fields: ["trainingEvaluationMinimumValue", "trainingEvaluationMaximumValue"]},
    {title: "Ustawienia dla departamentów", fields: ["sameManagerInManyDepartments"]},
    {title: "Ustawienia dla stanowisk", fields: ["positionEvaluationDefaultValue", "positionEvaluationMinimumValue", "positionEvaluationMaximumValue"]},
    {title: "Wagi oceny ogólnej pracownika", fields: ["employeeEvaluationSoftSkillsWeight", "employeeEvaluationPositionsWeight", "employeeEvaluationTrainingsWeight", "employeeEvaluationTrainingTestsWeight", "employeeEvaluationReviewsWeight", "employeeEvaluationEventsWeight"]},
    {title: "Ustawienia dla umów", fields: ["contractExpirationWarningDays", "employeeDocumentsRootPath"]},
    {title: "Globalne reguły planowania czasu pracy", fields: ["scheduleEmploymentMinimumDailyRestHours", "scheduleEmploymentMinimumWeeklyRestHours", "scheduleDefaultRequiredDaysOffPerWeek", "scheduleDefaultMaxConsecutiveWorkDays", "scheduleConsecutiveDaysWarningThreshold", "scheduleWeeklyHoursWarningPercent", "scheduleTrainingTestMonthlyFailureLimit", "workTimeUnexcusedAbsenceAlertThreshold", "scheduleDayOffMinimumAdvanceDays", "scheduleVacationMinimumAdvanceDays", "scheduleRuleOverrideRequireReason"]},
    {title: "RCP — terminale i kontrola czasu", fields: ["rcpLateToleranceMinutes", "rcpEarlyLeaveToleranceMinutes", "rcpMissingStartAlertMinutes", "rcpMaximumBreakMinutes"]},
    {title: "Eksport Wapro Gang", fields: ["gangExportCharset", "gangExportDelimiter", "gangExportIncludeHeader", "gangExportDateFormat", "gangExportTimeFormat", "gangExportColumns"]},
    {title: "Prognoza pogody", fields: ["weatherForecastEnabled", "weatherTimezone", "weatherLatitude", "weatherLongitude", "weatherProviderBaseUrl"]},
    {title: "Ustawienia zadań automatycznych", fields: ["certificationsExpirationCron"]}
  ];

  const numberFields = new Set([
    "infractionWeightMinimumValue", "infractionWeightMaximumValue", "commendationWeightMinimumValue", "commendationWeightMaximumValue",
    "skillWeightMinimumValue", "skillWeightMaximumValue", "skillEvaluationMinimumValue", "skillEvaluationMaximumValue",
    "trainingEvaluationMinimumValue", "trainingEvaluationMaximumValue", "positionEvaluationDefaultValue", "positionEvaluationMinimumValue",
    "positionEvaluationMaximumValue", "employeeEvaluationSoftSkillsWeight", "employeeEvaluationPositionsWeight", "employeeEvaluationTrainingsWeight",
    "employeeEvaluationTrainingTestsWeight", "employeeEvaluationReviewsWeight", "employeeEvaluationEventsWeight", "contractExpirationWarningDays",
    "scheduleEmploymentMinimumDailyRestHours", "scheduleEmploymentMinimumWeeklyRestHours", "scheduleDefaultRequiredDaysOffPerWeek",
    "scheduleDefaultMaxConsecutiveWorkDays", "scheduleConsecutiveDaysWarningThreshold", "scheduleWeeklyHoursWarningPercent",
    "scheduleTrainingTestMonthlyFailureLimit", "workTimeUnexcusedAbsenceAlertThreshold", "rcpLateToleranceMinutes",
    "rcpEarlyLeaveToleranceMinutes", "rcpMissingStartAlertMinutes", "rcpMaximumBreakMinutes", "scheduleDayOffMinimumAdvanceDays",
    "scheduleVacationMinimumAdvanceDays"
  ]);

  const optionalNumberFields = new Set(["weatherLatitude", "weatherLongitude"]);
  const booleanFields = new Set(["sameManagerInManyDepartments", "gangExportIncludeHeader", "scheduleRuleOverrideRequireReason", "weatherForecastEnabled"]);
  const requiredTextFields = new Set(["gangExportDelimiter", "gangExportDateFormat", "gangExportTimeFormat"]);
  const fullWidthFields = new Set(["employeeDocumentsRootPath", "gangExportColumns", "weatherProviderBaseUrl"]);

  const helperText = {
    employeeDocumentsRootPath: "Qstaff tworzy w tym katalogu osobne foldery pracowników i podfolder „Umowy”. Pole można wpisać ręcznie albo wskazać katalog przyciskiem Wybierz folder.",
    employeeEvaluationTrainingTestsWeight: "Średnia procentowa z wyników testów jest przeliczana na skalę ocen szkoleń i uwzględniana w ocenie ogólnej z osobną wagą.",
    employeeEvaluationReviewsWeight: "Oceny z kategorii Rozwój, Szkolenie, Umiejętności i Rozmowa oceniająca są najpierw liczone z wag poszczególnych wpisów, a następnie włączane do oceny ogólnej z tą wagą grupy.",
    scheduleDefaultRequiredDaysOffPerWeek: "Wartość globalna używana wtedy, gdy pracownik ani jego dział nie mają własnego ustawienia wymaganej liczby dni wolnych.",
    scheduleDefaultMaxConsecutiveWorkDays: "Wartość globalna używana wtedy, gdy pracownik ani jego dział nie mają własnego limitu kolejnych dni pracy.",
    scheduleConsecutiveDaysWarningThreshold: "Globalny próg ostrzeżenia. Dział może ustawić własny próg, który ma pierwszeństwo dla jego grafików i widoków dostępności.",
    scheduleDayOffMinimumAdvanceDays: "Globalne minimalne wyprzedzenie prośby o dzień wolny. Wartość ustawiona dla działu ma pierwszeństwo.",
    scheduleVacationMinimumAdvanceDays: "Globalne minimalne wyprzedzenie wniosku urlopowego. Wartość ustawiona dla działu ma pierwszeństwo.",
    scheduleTrainingTestMonthlyFailureLimit: "Wartość 0 wyłącza blokadę. Po osiągnięciu limitu pracownik nie może zostać przydzielony do zmiany.",
    workTimeUnexcusedAbsenceAlertThreshold: "Po osiągnięciu progu pracownik pojawia się w alertach na Tablicy.",
    rcpMissingStartAlertMinutes: "Po tym czasie zaplanowana zmiana bez odbicia RCP jest oznaczana jako ryzyko braku obsady.",
    rcpMaximumBreakMinutes: "Wartość 0 wyłącza alert. Pobyt w strefie oznaczonej „licz jako przerwę” zwiększa ten czas.",
    gangExportColumns: "Dozwolone klucze: EMPLOYEE_ID, LOGIN, PESEL, CARD_ID, DATE, ENTRY_TYPE, PLANNED_START, PLANNED_END, ACTUAL_START, ACTUAL_END, BREAK_MINUTES, WORKED_MINUTES, DELTA_MINUTES, LATE_MINUTES, EARLY_LEAVE_MINUTES, OVERTIME_MINUTES, ABSENCE_TYPE, ATTENDANCE_STATUS, SOURCE, POSITION_NAME. Kolejność wpisów określa kolejność kolumn.",
    weatherForecastEnabled: "Po włączeniu kierownik może pobierać pogodę do widoku sprzedaży i budżetu pracy. Współrzędne określają lokalizację Parku.",
    weatherLatitude: "Podaj dodatnią wartość dziesiętną od 0 do 90° i wybierz kierunek N (północ) albo S (południe). Pole jest wymagane tylko przy włączonej integracji.",
    weatherLongitude: "Podaj dodatnią wartość dziesiętną od 0 do 180° i wybierz kierunek E (wschód) albo W (zachód). Pole jest wymagane tylko przy włączonej integracji.",
    weatherTimezone: "Domyślnie Europe/Warsaw.",
    weatherProviderBaseUrl: "Domyślnie używany jest publiczny endpoint Open-Meteo. Adres można zmienić na zgodny endpoint dostawcy lub własnej instancji."
  };

  const saveFields = [
    ...Object.keys(itemDictionary),
    "scheduleStatusAvailableColor", "scheduleStatusWeekendOnlyColor", "scheduleStatusFreeDaysOnlyColor", "scheduleStatusNoContractColor",
    "scheduleStatusAvailableFontColor", "scheduleStatusWeekendOnlyFontColor", "scheduleStatusFreeDaysOnlyFontColor", "scheduleStatusNoContractFontColor"
  ];

  const stateForCrud = {itemsList, setItems};
  const isDirty = useMemo(() => JSON.stringify(currentItem) !== JSON.stringify(backupItem), [currentItem, backupItem]);

  useEffect(() => {
    const load = async () => {
      const response = await getItems(dataUrl);
      if (response.status === 200) {
        const item = response.data || {};
        setItems([item]);
        setCurrentItem(item);
        setBackupItem(JSON.parse(JSON.stringify(item)));
        setWeatherDirections({
          latitude: Number(item.weatherLatitude) < 0 ? "S" : "N",
          longitude: Number(item.weatherLongitude) < 0 ? "W" : "E"
        });
      } else {
        toastError("Nie udało się pobrać ustawień aplikacji");
      }
      setLoading(false);
    };
    load();
  }, []);

  const setField = (field, value) => {
    setCurrentItem(prev => ({...prev, [field]: value}));
    setInvalidFields(prev => {
      if (!prev.has(field)) return prev;
      const next = new Set(prev);
      next.delete(field);
      return next;
    });
  };

  const validate = () => {
    const invalid = new Set();
    numberFields.forEach(field => {
      const value = currentItem[field];
      if (value === "" || value === null || value === undefined || !Number.isFinite(Number(value))) invalid.add(field);
    });
    requiredTextFields.forEach(field => {
      if (!String(currentItem[field] ?? "").trim()) invalid.add(field);
    });
    optionalNumberFields.forEach(field => {
      const value = currentItem[field];
      if (value !== "" && value !== null && value !== undefined && !Number.isFinite(Number(value))) invalid.add(field);
    });
    if (currentItem.weatherForecastEnabled) {
      if (currentItem.weatherLatitude === "" || currentItem.weatherLatitude === null || currentItem.weatherLatitude === undefined) invalid.add("weatherLatitude");
      if (currentItem.weatherLongitude === "" || currentItem.weatherLongitude === null || currentItem.weatherLongitude === undefined) invalid.add("weatherLongitude");
    }
    if (currentItem.weatherLatitude !== "" && currentItem.weatherLatitude !== null && currentItem.weatherLatitude !== undefined && (Number(currentItem.weatherLatitude) < -90 || Number(currentItem.weatherLatitude) > 90)) invalid.add("weatherLatitude");
    if (currentItem.weatherLongitude !== "" && currentItem.weatherLongitude !== null && currentItem.weatherLongitude !== undefined && (Number(currentItem.weatherLongitude) < -180 || Number(currentItem.weatherLongitude) > 180)) invalid.add("weatherLongitude");
    setInvalidFields(invalid);
    if (invalid.size > 0) {
      toastError("Uzupełnij poprawnie zaznaczone pola");
      return false;
    }
    return true;
  };

  const restore = () => {
    if (!isDirty || saving) return;
    setCurrentItem(JSON.parse(JSON.stringify(backupItem)));
    setWeatherDirections({
      latitude: Number(backupItem.weatherLatitude) < 0 ? "S" : "N",
      longitude: Number(backupItem.weatherLongitude) < 0 ? "W" : "E"
    });
    setInvalidFields(new Set());
  };

  const save = async () => {
    if (!isDirty || saving || !validate()) return;
    const payload = {id: currentItem.id || 1};
    saveFields.forEach(field => {
      payload[field] = currentItem[field];
    });
    payload.employeeDocumentsRootPath = currentItem.employeeDocumentsRootPath || "";
    setSaving(true);
    const response = await updateItem(`${dataUrl}/${payload.id}`, payload, stateForCrud);
    setSaving(false);
    if (response?.status >= 200 && response?.status < 300) {
      const saved = response.data || payload;
      setCurrentItem(saved);
      setBackupItem(JSON.parse(JSON.stringify(saved)));
      setWeatherDirections({
        latitude: Number(saved.weatherLatitude) < 0 ? "S" : "N",
        longitude: Number(saved.weatherLongitude) < 0 ? "W" : "E"
      });
      setItems([saved]);
      setInvalidFields(new Set());
      toastSuccess("Ustawienia zostały zapisane");
    } else {
      toastError(typeof response?.data === "string" ? response.data : response?.data?.message || "Nie udało się zapisać ustawień");
    }
  };

  const loadDirectory = async path => {
    setFolderBrowserLoading(true);
    try {
      const response = await axiosPrivate.get("/settings/directories", {params: path ? {path} : {}});
      setFolderBrowser(response.data || {currentPath: null, parentPath: null, directories: [], roots: true});
      return true;
    } catch (e) {
      toastError(typeof e?.response?.data === "string" ? e.response.data : e?.response?.data?.message || "Nie można odczytać katalogów");
      return false;
    } finally {
      setFolderBrowserLoading(false);
    }
  };

  const openDirectoryBrowser = async () => {
    setFolderBrowserOpen(true);
    const configuredPath = String(currentItem.employeeDocumentsRootPath || "").trim();
    if (configuredPath) {
      const loaded = await loadDirectory(configuredPath);
      if (!loaded) await loadDirectory(null);
    } else {
      await loadDirectory(null);
    }
  };

  const selectCurrentDirectory = () => {
    if (!folderBrowser.currentPath) return;
    setField("employeeDocumentsRootPath", folderBrowser.currentPath);
    setFolderBrowserOpen(false);
  };

  const folderLabel = path => {
    if (!path) return "";
    const normalized = String(path).replace(/[\\/]+$/, "");
    const parts = normalized.split(/[\\/]/).filter(Boolean);
    return parts.length ? parts[parts.length - 1] : path;
  };

  const renderControl = field => {
    const value = currentItem[field];
    const invalid = invalidFields.has(field);

    if (booleanFields.has(field)) {
      return (
        <label className="settings-preview-switch" htmlFor={field}>
          <input id={field} type="checkbox" checked={!!value} onChange={e => setField(field, e.target.checked)}/>
          <span className={`settings-preview-badge ${value ? "is-yes" : "is-no"}`}>{value ? "TAK" : "NIE"}</span>
        </label>
      );
    }

    if (field === "gangExportCharset") {
      return (
        <select id={field} className={`settings-preview-input ${invalid ? "is-invalid" : ""}`} value={value || "WINDOWS-1250"} onChange={e => setField(field, e.target.value)}>
          <option value="WINDOWS-1250">Windows-1250</option>
          <option value="UTF-8">UTF-8</option>
        </select>
      );
    }

    if (field === "gangExportColumns") {
      return (
        <textarea id={field} className={`settings-preview-input settings-preview-textarea settings-preview-code ${invalid ? "is-invalid" : ""}`} rows="5" value={value || ""} onChange={e => setField(field, e.target.value)}/>
      );
    }

    if (field === "employeeDocumentsRootPath") {
      return (
        <div className="settings-folder-path-control">
          <input id={field} className={`settings-preview-input settings-preview-path ${invalid ? "is-invalid" : ""}`} type="text" value={value ?? ""} onChange={e => setField(field, e.target.value)}/>
          <Button type="button" variant="outline-secondary" size="sm" onClick={openDirectoryBrowser}>Wybierz folder</Button>
        </div>
      );
    }

    if (field === "weatherLatitude" || field === "weatherLongitude") {
      const latitude = field === "weatherLatitude";
      const directionKey = latitude ? "latitude" : "longitude";
      const direction = weatherDirections[directionKey];
      const maximum = latitude ? 90 : 180;
      const directionOptions = latitude
        ? [{value: "N", label: "N — północ"}, {value: "S", label: "S — południe"}]
        : [{value: "E", label: "E — wschód"}, {value: "W", label: "W — zachód"}];
      const displayValue = value === "" || value === null || value === undefined ? "" : Math.abs(Number(value));

      const updateCoordinate = rawValue => {
        if (rawValue === "") {
          setField(field, "");
          return;
        }
        const absoluteValue = Math.abs(Number(rawValue));
        const negative = direction === "S" || direction === "W";
        setField(field, negative ? -absoluteValue : absoluteValue);
      };

      const updateDirection = nextDirection => {
        setWeatherDirections(prev => ({...prev, [directionKey]: nextDirection}));
        if (value === "" || value === null || value === undefined) return;
        const absoluteValue = Math.abs(Number(value));
        const negative = nextDirection === "S" || nextDirection === "W";
        setField(field, negative ? -absoluteValue : absoluteValue);
      };

      return (
        <div className="settings-coordinate-control">
          <input
            id={field}
            className={`settings-preview-input settings-preview-number ${invalid ? "is-invalid" : ""}`}
            type="number"
            min="0"
            max={maximum}
            step="0.000001"
            inputMode="decimal"
            value={displayValue}
            onChange={e => updateCoordinate(e.target.value)}
          />
          <select
            id={`${field}Direction`}
            className="settings-preview-input settings-coordinate-direction"
            value={direction}
            onChange={e => updateDirection(e.target.value)}
            aria-label={latitude ? "Kierunek szerokości geograficznej" : "Kierunek długości geograficznej"}
          >
            {directionOptions.map(option => <option key={option.value} value={option.value}>{option.label}</option>)}
          </select>
        </div>
      );
    }

    if (numberFields.has(field) || optionalNumberFields.has(field)) {
      return (
        <input id={field} className={`settings-preview-input settings-preview-number ${invalid ? "is-invalid" : ""}`} type="number" value={value ?? ""} onChange={e => setField(field, e.target.value === "" ? "" : Number(e.target.value))}/>
      );
    }

    return (
      <input id={field} className={`settings-preview-input ${invalid ? "is-invalid" : ""}`} type="text" value={value ?? ""} onChange={e => setField(field, e.target.value)}/>
    );
  };

  const renderSection = section => (
    <section key={section.title} className="settings-preview-section">
      <div className="settings-preview-section-title">{section.title}</div>
      <div className="settings-preview-fields">
        {section.fields.map(field => (
          <div key={field} className={`settings-preview-field ${fullWidthFields.has(field) ? "is-wide" : ""}`}>
            <label className="settings-preview-label" htmlFor={field}>{itemDictionary[field] || field}</label>
            <div className="settings-preview-value">{renderControl(field)}</div>
            {helperText[field] && <div className="settings-preview-help">{helperText[field]}</div>}
          </div>
        ))}
      </div>
    </section>
  );

  const normalizeHex = value => /^#[0-9a-f]{6}$/i.test(value || "") ? value : "#ffffff";
  const colorRows = [
    {label: "DOSTĘPNY", background: "scheduleStatusAvailableColor", font: "scheduleStatusAvailableFontColor"},
    {label: "WEEKENDOWY", background: "scheduleStatusWeekendOnlyColor", font: "scheduleStatusWeekendOnlyFontColor"},
    {label: "TYLKO W DNI WOLNE", background: "scheduleStatusFreeDaysOnlyColor", font: "scheduleStatusFreeDaysOnlyFontColor"},
    {label: "BRAK UMOWY", background: "scheduleStatusNoContractColor", font: "scheduleStatusNoContractFontColor"}
  ];

  if (loading) {
    return (
      <div id="layoutSidenav_content" className="settings-page">
        <AppComponentCardHeader title="Ustawienia globalne aplikacji"/>
        <div className="container-fluid px-4 py-3 settings-page-container"><LoadingDataDiv/></div>
      </div>
    );
  }

  return (
    <div id="layoutSidenav_content" className="settings-page">
      <ToastContainer/>
      <AppComponentCardHeader title="Ustawienia globalne aplikacji"/>
      <div className="container-fluid px-4 py-3 settings-page-container">
        <div className="card shadow-sm bg-white rounded-2">
          <div className="settings-preview">
            <div className="settings-preview-header">
              <div>
                <strong>Podgląd ustawień globalnych</strong>
                <span>Ten sam widok służy teraz również do edycji. Zapis jest dostępny dopiero po zmianie wartości.</span>
              </div>
              <div className="d-flex gap-2">
                <Button type="button" variant="outline-secondary" onClick={restore} disabled={!isDirty || saving}>Przywróć</Button>
                <Button type="button" onClick={save} disabled={!isDirty || saving}>{saving ? "Zapisywanie..." : "Zapisz"}</Button>
              </div>
            </div>

            <div className="settings-preview-grid">
              {sections.map(renderSection)}

              <section className="settings-preview-section settings-preview-colors">
                <div className="settings-preview-section-title">Kolory w grafiku dostępności</div>
                <div className="settings-preview-color-samples settings-preview-color-editors">
                  {colorRows.map(row => (
                    <div key={row.label} className="settings-color-preview-editor">
                      <div className="settings-color-preview-sample" style={{backgroundColor: currentItem[row.background], color: currentItem[row.font]}}>{row.label}</div>
                      <div className="settings-color-preview-controls">
                        <label>Tło <input type="color" value={normalizeHex(currentItem[row.background])} onChange={e => setField(row.background, e.target.value)}/></label>
                        <label>Czcionka <input type="color" value={normalizeHex(currentItem[row.font])} onChange={e => setField(row.font, e.target.value)}/></label>
                      </div>
                    </div>
                  ))}
                </div>
              </section>
            </div>
          </div>
        </div>
      </div>

      <Modal show={folderBrowserOpen} onHide={() => setFolderBrowserOpen(false)} size="lg" centered>
        <Modal.Header closeButton>
          <Modal.Title>Wybierz katalog dokumentów pracowników</Modal.Title>
        </Modal.Header>
        <Modal.Body className="settings-folder-browser">
          <div className="settings-folder-browser-toolbar">
            <Button type="button" variant="outline-secondary" size="sm" onClick={() => loadDirectory(folderBrowser.parentPath)} disabled={folderBrowserLoading || (!folderBrowser.currentPath && folderBrowser.roots)}>Poziom wyżej</Button>
            <Button type="button" variant="outline-secondary" size="sm" onClick={() => loadDirectory(null)} disabled={folderBrowserLoading}>Dyski / katalog główny</Button>
            <div className="settings-folder-current-path">{folderBrowser.currentPath || "Wybierz dysk lub katalog główny"}</div>
          </div>
          {folderBrowserLoading ? (
            <div className="settings-folder-loading"><Spinner animation="border" size="sm"/> Odczytywanie katalogów...</div>
          ) : (
            <div className="settings-folder-list">
              {(folderBrowser.directories || []).map(path => (
                <button key={path} type="button" className="settings-folder-entry" onDoubleClick={() => loadDirectory(path)} onClick={() => loadDirectory(path)}>
                  <span className="settings-folder-icon">📁</span>
                  <span>{folderLabel(path)}</span>
                </button>
              ))}
              {(!folderBrowser.directories || folderBrowser.directories.length === 0) && <div className="settings-folder-empty">Brak dostępnych podkatalogów.</div>}
            </div>
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setFolderBrowserOpen(false)}>Anuluj</Button>
          <Button variant="primary" onClick={selectCurrentDirectory} disabled={!folderBrowser.currentPath || folderBrowserLoading}>Wybierz ten folder</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default Settings;




